import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { dramas } from "@/mocks/dramas";

const NAV_ITEMS = [
  { to: "/", label: "首页" },
  { to: "/category", label: "分类" },
  { to: "/special", label: "专题" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return [];
    return dramas
      .filter((drama) => {
        const inTitle = drama.title.includes(kw) || drama.subtitle.includes(kw);
        const inCast = drama.cast.some((name) => name.includes(kw));
        const inRoles = drama.roles.some((role) => role.name.includes(kw) || role.actor.includes(kw));
        const inTags = drama.tags.some((tag) => tag.includes(kw));
        return inTitle || inCast || inRoles || inTags;
      })
      .slice(0, 6);
  }, [keyword]);

  useEffect(() => {
    function onDocClick(event: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function goDetail(id: string) {
    setKeyword("");
    setOpen(false);
    setMobileSearch(false);
    navigate(`/detail/${id}`);
  }

  function submitSearch(event: React.FormEvent) {
    event.preventDefault();
    if (results.length > 0) {
      goDetail(results[0].id);
    } else if (keyword.trim()) {
      navigate(`/category?q=${encodeURIComponent(keyword.trim())}`);
      setOpen(false);
    }
  }

  const searchBox = (
    <form onSubmit={submitSearch} className="relative w-full">
      <div className="flex h-10 items-center rounded-full border border-background-200 bg-background-50 px-4 focus-within:border-primary-400">
        <i className="ri-search-line text-base text-foreground-400"></i>
        <input
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          type="text"
          name="keyword"
          aria-label="站内搜索"
          placeholder="搜索剧名或角色名..."
          className="ml-2 h-full w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
        />
        {keyword && (
          <button
            type="button"
            aria-label="清空搜索"
            onClick={() => setKeyword("")}
            className="text-foreground-400 transition-colors hover:text-primary-500"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        )}
      </div>
      {open && keyword.trim() && (
        <div className="absolute left-0 right-0 top-12 z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-none">
          {results.length > 0 ? (
            results.map((drama) => (
              <button
                key={drama.id}
                type="button"
                onClick={() => goDetail(drama.id)}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-background-100"
              >
                <span className="h-12 w-9 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={drama.cover} alt={drama.title} className="h-full w-full object-top object-cover" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground-900">{drama.title}</span>
                  <span className="block truncate text-xs text-foreground-500">主演：{drama.cast.join(" / ")}</span>
                </span>
                <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
              </button>
            ))
          ) : (
            <p className="px-4 py-3 text-center text-xs text-foreground-500">未找到匹配的剧集，试试其他关键词</p>
          )}
        </div>
      )}
    </form>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 items-center gap-4 px-4 md:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-semibold text-foreground-950">迷人的保姆</span>
            <span className="mt-1 text-[10px] tracking-widest text-foreground-500">影视剧集资讯站</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-100 font-medium text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div ref={boxRef} className="ml-auto hidden w-72 lg:block">
          {searchBox}
        </div>
        <div className="ml-auto hidden md:block lg:hidden">
          <button
            type="button"
            aria-label="搜索"
            onClick={() => setMobileSearch((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-200 text-foreground-700 hover:border-primary-400 hover:text-primary-500"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
        </div>

        <button
          type="button"
          aria-label="菜单"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 md:hidden"
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {mobileSearch && (
        <div className="hidden border-t border-background-200 px-4 py-3 md:block lg:hidden">
          {searchBox}
        </div>
      )}

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <div className="mb-3">{searchBox}</div>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `cursor-pointer rounded-md px-3 py-3 text-sm ${
                    isActive ? "bg-primary-100 font-medium text-primary-700" : "text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}